import type {
  SharedList,
  SharedListMember,
  SharedListWithRole,
  SharedListCreateInput,
  SharedListMemberInviteInput,
  SharedListPermission,
} from '@danny-tasks/shared';
import { fetchApi } from './fetch';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { SharedList, SharedListMember, SharedListWithRole, SharedListPermission };

export interface SharedListTask {
  id: string;
  content: string;
  isCompleted: boolean;
  priority?: number;
  projectId?: string | null;
  parentId?: string | null;
  dueDate?: string | null;
  subtasks: SharedListTask[];
}

// Extended member type — API response includes display name and role alias
export type SharedListMemberDisplay = SharedListMember & {
  name: string;
  role: string;
};

export type SharedListDetail = SharedList & {
  members: SharedListMemberDisplay[];
  tasks: SharedListTask[];
  myRole: string;
};

export interface SharedListInvitePreview {
  listId: string;
  listName: string;
  inviterName: string;
  role: SharedListPermission;
}

export interface SharedListInviteResult {
  listId: string;
  token: string;
}

// ─── Core CRUD ────────────────────────────────────────────────────────────────

export async function createSharedList(input: SharedListCreateInput): Promise<SharedList> {
  return fetchApi<SharedList>('/shared-lists', { method: 'POST', body: JSON.stringify(input) });
}

export async function listSharedLists(): Promise<SharedListWithRole[]> {
  return fetchApi<SharedListWithRole[]>('/shared-lists');
}

export const getSharedLists = listSharedLists;

export async function getSharedList(listId: string): Promise<SharedListDetail> {
  return fetchApi<SharedListDetail>(`/shared-lists/${listId}`);
}

export async function updateSharedList(
  listId: string,
  patch: { name?: string; linkPermission?: SharedListPermission | null },
): Promise<SharedList> {
  return fetchApi<SharedList>(`/shared-lists/${listId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function renameSharedList(listId: string, name: string): Promise<SharedList> {
  return updateSharedList(listId, { name });
}

export async function deleteSharedList(listId: string): Promise<void> {
  await fetchApi<void>(`/shared-lists/${listId}`, { method: 'DELETE' });
}

// ─── Members ──────────────────────────────────────────────────────────────────

export async function inviteSharedListMember(
  listId: string,
  input: SharedListMemberInviteInput,
): Promise<SharedListMember> {
  return fetchApi<SharedListMember>(`/shared-lists/${listId}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function inviteToSharedList(
  listId: string,
  email: string,
  permission: string,
): Promise<SharedListMember> {
  return inviteSharedListMember(listId, { email, permission: permission as SharedListPermission });
}

export async function updateSharedListMember(
  listId: string,
  memberId: string,
  permission: SharedListPermission,
): Promise<SharedListMember> {
  return fetchApi<SharedListMember>(`/shared-lists/${listId}/members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify({ permission }),
  });
}

export async function changeSharedListMemberRole(
  listId: string,
  memberId: string,
  permission: string,
): Promise<SharedListMember> {
  return updateSharedListMember(listId, memberId, permission as SharedListPermission);
}

export async function removeSharedListMember(listId: string, memberId: string): Promise<void> {
  await fetchApi<void>(`/shared-lists/${listId}/members/${memberId}`, { method: 'DELETE' });
}

export async function leaveSharedList(listId: string): Promise<void> {
  await fetchApi<void>(`/shared-lists/${listId}/members/me`, { method: 'DELETE' });
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

export async function createTaskInSharedList(
  listId: string,
  content: string,
  priority?: number,
): Promise<SharedListTask> {
  return fetchApi<SharedListTask>(`/shared-lists/${listId}/tasks`, {
    method: 'POST',
    body: JSON.stringify({ content, ...(priority ? { priority } : {}) }),
  });
}

export async function createSharedListTask(
  listId: string,
  input: { content: string; priority?: number },
): Promise<SharedListTask> {
  return fetchApi<SharedListTask>(`/shared-lists/${listId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateSharedListTask(
  listId: string,
  taskId: string,
  updates: Partial<SharedListTask>,
): Promise<SharedListTask> {
  return fetchApi<SharedListTask>(`/shared-lists/${listId}/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteSharedListTask(listId: string, taskId: string): Promise<void> {
  await fetchApi<void>(`/shared-lists/${listId}/tasks/${taskId}`, { method: 'DELETE' });
}

export async function resetSharedListTasks(listId: string): Promise<void> {
  await fetchApi<void>(`/shared-lists/${listId}/tasks/reset`, { method: 'POST' });
}

// ─── Invites ──────────────────────────────────────────────────────────────────

export async function getSharedListInvitePreview(
  inviteCode: string,
): Promise<SharedListInvitePreview> {
  return fetchApi<SharedListInvitePreview>(`/shared-lists/invite/${inviteCode}/preview`);
}

export async function acceptSharedListInvite(
  inviteCode: string,
): Promise<SharedListInviteResult> {
  return fetchApi<SharedListInviteResult>(`/shared-lists/invite/${inviteCode}/accept`, {
    method: 'POST',
  });
}
